import React, { useState } from 'react';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid';
import {
  Box,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Divider,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { ProcessingSession, ProcessingFilters } from '../types/api';

interface ProcessingTableProps {
  sessions: ProcessingSession[];
  total: number;
  loading: boolean;
  filters: ProcessingFilters;
  onFiltersChange: (filters: ProcessingFilters) => void;
}

export default function ProcessingTable({
  sessions,
  total,
  loading,
  filters,
  onFiltersChange,
}: ProcessingTableProps) {
  const [detailsModal, setDetailsModal] = useState<{
    open: boolean;
    session: ProcessingSession | null;
  }>({ open: false, session: null });

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Не указано';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (seconds: number | undefined) => {
    if (!seconds) return 'Не указано';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}м ${secs}с`;
  };

  const formatFileSize = (bytes: number | undefined) => {
    if (!bytes) return 'Не указано';
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
  };

  const getStatusColor = (status: string | undefined) => {
    switch (status) {
      case 'success': return 'success';
      case 'download_failed': 
      case 'processing_failed': return 'error';
      case 'pending':
      case 'downloading':
      case 'processing': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string | undefined) => {
    switch (status) {
      case 'success': return <CheckCircleIcon fontSize="small" />;
      case 'download_failed': 
      case 'processing_failed': return <ErrorIcon fontSize="small" />;
      case 'pending': return <ScheduleIcon fontSize="small" />;
      case 'processing': return <PlayArrowIcon fontSize="small" />;
      default: return null;
    }
  };

  const getStatusText = (status: string | undefined) => {
    switch (status) {
      case 'success': return 'Успешно';
      case 'download_failed': return 'Ошибка загрузки';
      case 'processing_failed': return 'Ошибка обработки';
      case 'pending': return 'Ожидание';
      case 'downloading': return 'Загрузка';
      case 'processing': return 'Обработка';
      default: return status || 'Не указано';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'session_id',
      headerName: 'Session ID',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
          {params.value.slice(-8)}...
        </Box>
      ),
    },
    {
      field: 'user_id',
      headerName: 'User ID',
      width: 80,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'source_type',
      headerName: 'Источник',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value === 'url' ? 'URL' : 'Telegram'}
          variant="outlined"
          size="small"
          color={params.value === 'url' ? 'primary' : 'secondary'}
        />
      ),
    },
    {
      field: 'specific_source',
      headerName: 'Платформа',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ textTransform: 'capitalize' }}>
          {params.value || 'Не указано'}
        </Box>
      ),
    },
    {
      field: 'original_identifier',
      headerName: 'Идентификатор',
      width: 200,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Box sx={{ 
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '0.875rem'
          }}>
            {params.value}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'final_status',
      headerName: 'Статус',
      width: 140,
      renderCell: (params) => (
        <Chip
          icon={getStatusIcon(params.value)}
          label={getStatusText(params.value)}
          color={getStatusColor(params.value)}
          size="small"
          variant={params.value ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'started_at',
      headerName: 'Начато',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ fontSize: '0.875rem' }}>
          {formatDate(params.value)}
        </Box>
      ),
    },
    {
      field: 'total_duration',
      headerName: 'Длительность',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ fontSize: '0.875rem' }}>
          {params.value ? formatDuration(params.value) : '—'}
        </Box>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Действия',
      width: 100,
      getActions: (params: GridRowParams<ProcessingSession>) => [
        <GridActionsCellItem
          key="view"
          icon={
            <Tooltip title="Подробности">
              <ViewIcon />
            </Tooltip>
          }
          label="Подробности"
          onClick={() => setDetailsModal({ open: true, session: params.row })}
        />,
      ],
    },
  ];

  const handlePaginationChange = (model: GridPaginationModel) => {
    onFiltersChange({
      ...filters,
      page: model.page + 1,
      size: model.pageSize,
    });
  };

  return (
    <>
      <DataGrid
        rows={sessions}
        columns={columns}
        loading={loading}
        pageSizeOptions={[10, 20, 50, 100]}
        pagination
        paginationMode="server"
        rowCount={total}
        paginationModel={{
          page: (filters.page || 1) - 1,
          pageSize: filters.size || 20,
        }}
        onPaginationModelChange={handlePaginationChange}
        disableRowSelectionOnClick
        sx={{
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
        localeText={{
          noRowsLabel: 'Нет данных',
          noResultsOverlayLabel: 'Результаты не найдены',
          MuiTablePagination: {
            labelRowsPerPage: 'Строк на странице:',
            labelDisplayedRows: ({ from, to, count }) =>
              `${from}–${to} из ${count !== -1 ? count : `более чем ${to}`}`,
          },
        }}
      />

      {/* Модальное окно с деталями сессии */}
      <Dialog
        open={detailsModal.open}
        onClose={() => setDetailsModal({ open: false, session: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PlayArrowIcon />
            <Typography variant="h6">
              Сессия обработки
            </Typography>
            {detailsModal.session?.final_status && (
              <Chip
                icon={getStatusIcon(detailsModal.session.final_status)}
                label={getStatusText(detailsModal.session.final_status)}
                color={getStatusColor(detailsModal.session.final_status)}
                size="small"
              />
            )}
          </Box>
        </DialogTitle>

        <DialogContent>
          {detailsModal.session && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Основная информация
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Session ID
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  {detailsModal.session.session_id}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Пользователь
                </Typography>
                <Typography variant="body1">
                  ID: {detailsModal.session.user_id}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Источник
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Chip
                    label={detailsModal.session.source_type === 'url' ? 'URL' : 'Telegram'}
                    variant="outlined"
                    size="small"
                  />
                  {detailsModal.session.specific_source && (
                    <Chip
                      label={detailsModal.session.specific_source}
                      variant="outlined"
                      size="small"
                    />
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Размер файла
                </Typography>
                <Typography variant="body1">
                  {formatFileSize(detailsModal.session.original_file_size)}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Идентификатор источника
                </Typography>
                <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                  {detailsModal.session.original_identifier}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Временные метки
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Начато
                </Typography>
                <Typography variant="body1">
                  {formatDate(detailsModal.session.started_at)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Завершено
                </Typography>
                <Typography variant="body1">
                  {detailsModal.session.completed_at 
                    ? formatDate(detailsModal.session.completed_at)
                    : 'В процессе'
                  }
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Общая длительность
                </Typography>
                <Typography variant="body1">
                  {detailsModal.session.total_duration 
                    ? formatDuration(detailsModal.session.total_duration)
                    : 'Не завершено'
                  }
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Попыток загрузки
                </Typography>
                <Typography variant="body1">
                  {detailsModal.session.total_download_attempts || 0}
                </Typography>
              </Grid>

              {(detailsModal.session.error_stage || detailsModal.session.error_message) && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom color="error">
                      Информация об ошибке
                    </Typography>
                  </Grid>

                  {detailsModal.session.error_stage && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Этап ошибки
                      </Typography>
                      <Chip
                        label={detailsModal.session.error_stage}
                        color="error"
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                  )}

                  {detailsModal.session.error_message && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Сообщение об ошибке
                      </Typography>
                      <Typography variant="body1" color="error">
                        {detailsModal.session.error_message}
                      </Typography>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDetailsModal({ open: false, session: null })}>
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}